/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// // Define product variants (replace with actual variant IDs)
// const SMITH_MACHINE_VARIANT_ID = "50426855326001"; // Smith Machine variant ID
// const GIFT_PRODUCT_VARIANT_ID = "50484894564657"; // Compact Walking Treadmill variant ID

// // Function to add a product to the cart
// function addToCart(variantId, quantity = 1) {
//     return fetch("/cart/add.js", {
//         method: "POST",
//         headers: {
//             "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//             id: variantId,
//             quantity: quantity,
//         }),
//     }).then((response) => response.json());
// }

// // Function to remove a product from the cart
// function removeFromCart(variantId) {
//     return fetch("/cart/change.js", {
//         method: "POST",
//         headers: {
//             "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//             id: variantId,
//             quantity: 0,
//         }),
//     }).then((response) => response.json());
// }

// // Function to check the cart and manage the gift product
// function manageGiftProduct() {
//     fetch("/cart.js")
//         .then((response) => response.json())
//         .then((cart) => {
//             const hasSmithMachine = cart.items.some(
//                 (item) => item.variant_id === parseInt(SMITH_MACHINE_VARIANT_ID)
//             );

//             const hasGiftProduct = cart.items.some(
//                 (item) => item.variant_id === parseInt(GIFT_PRODUCT_VARIANT_ID)
//             );

//             if (hasSmithMachine && !hasGiftProduct) {
//                 // Add gift product if Smith Machine is in the cart and gift is not
//                 addToCart(GIFT_PRODUCT_VARIANT_ID, 1).then(() => {
//                     // Refresh the cart drawer after adding the gift product
//                     document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
//                         bubbles: true
//                     }));
//                 });
//             } else if (!hasSmithMachine && hasGiftProduct) {
//                 // Remove gift product if Smith Machine is not in the cart but gift is
//                 removeFromCart(GIFT_PRODUCT_VARIANT_ID).then(() => {
//                     // Refresh the cart drawer after removing the gift product
//                     document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
//                         bubbles: true
//                     }));
//                 });
//             }
//         });
// }

// // Event listener for cart updates
// document.addEventListener("cart:updated", function(event) {
//     var cart = event.detail.cart; // Get the updated content of the cart
//     manageGiftProduct(cart);
// });

// // Trigger check when the page loads
// manageGiftProduct();


  // Function to equalize product title heights per row
function equalizeProductTitles() {
  const titles = document.querySelectorAll('.product-item-meta__title');
  
  if (titles.length === 0) return;
  
  // Reset heights first
  titles.forEach(title => {
    title.style.height = 'auto';
  });
  
  // Group titles by their vertical position (row)
  const rows = [];
  let currentRow = [];
  let currentTop = null;
  
  titles.forEach(title => {
    const rect = title.getBoundingClientRect();
    const top = Math.round(rect.top);
    
    // If this is a new row (different top position)
    if (currentTop === null || Math.abs(top - currentTop) > 5) {
      if (currentRow.length > 0) {
        rows.push(currentRow);
      }
      currentRow = [title];
      currentTop = top;
    } else {
      // Same row
      currentRow.push(title);
    }
  });
  
  // Push the last row
  if (currentRow.length > 0) {
    rows.push(currentRow);
  }
  
  // Equalize heights within each row
  rows.forEach(row => {
    let maxHeight = 0;
    
    // Find max height in this row
    row.forEach(title => {
      const height = title.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });
    
    // Apply max height to all titles in this row
    row.forEach(title => {
      title.style.height = `${maxHeight}px`;
    });
  });
}

// Run on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', equalizeProductTitles);
} else {
  equalizeProductTitles();
}

// Re-run on window resize (debounced)
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(equalizeProductTitles, 250);
});

// Optional: Run when images load (in case they affect layout)
window.addEventListener('load', equalizeProductTitles);

// For dynamic content (if products load via AJAX)
// Create a MutationObserver to watch for new products
const observer = new MutationObserver((mutations) => {
  let shouldEqualize = false;
  
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1 && 
          (node.classList?.contains('product-item') || 
           node.querySelector?.('.product-item'))) {
        shouldEqualize = true;
      }
    });
  });
  
  if (shouldEqualize) {
    setTimeout(equalizeProductTitles, 100);
  }
});

// Start observing the document for product additions
observer.observe(document.body, {
  childList: true,
  subtree: true
});

// Export function for manual calls if needed
window.equalizeProductTitles = equalizeProductTitles;

